import { ImportType } from './../types/imports';
import { CompanyTabKeys, MEMBER_TABS } from '../constants/tabs';
import { atom, atomFamily } from 'recoil';
import { selector, selectorFamily } from './recoilSelectors';
import { PAGE } from '../constants/pages';
import { atomWithPersistence, storageEffectForKey } from '../utils/persistence';
import { dayOfMinus, dayOfToday } from "../utils/time";
import { accountBlockedState, trialEndedForAdminState } from "./accountState";
import { Day, EMPTY_UID, PageType, Timestamp, Uid, UidDay } from "../types/core";
import { actionsIsOpenState, windowIsMobileState } from "./skeletonState";
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from "../constants/app";
import { memberUidsState } from "./membersState";
import { MessageInstance } from "antd/es/message/interface";
import { NotificationInstance } from "antd/es/notification/interface";
import { drawModeState, isLocationDrawMode } from "./drawModeState";
import { MemberField } from "../types/fields";
import { Activity } from "../types/notification";
import { Member } from "../types/member";
import { Job } from "../types/job";
import { Constraint } from "../constants/routing";
import { isGoogleProviderState } from './mapState';
import { isSafari } from "../utils/browser";
import { findAssigneeForJobState } from "./viewState";
import { layoutFoldsState } from "./layoutFoldState";

export const pageState = atomWithPersistence<PageType>('app.page.current', PAGE.LIVE);

export const memberActiveTabState = atom({
    key: 'member.tab.active',
    default: MEMBER_TABS.profile
});

export const selectedTimeFrameState = atom({
    key: 'app.time.selection',
    default: [0, 60 * 24],
});

export const selectedDayState = atomWithPersistence(
    'app.day.selection',
    dayOfToday()
);

export const selectedDayFrameState = atom<[Day, Day]>({
    key: "app.day.frame.selection",
    default: [dayOfMinus(6), dayOfToday()]
});

export const dayState = selector({
    key: 'app.day',
    get: ({ get }) => {
        return get(selectedDayState);
    }
});

export const editingMemberIdState = atom({
    key: 'editing.member.id',
    default: EMPTY_UID
});

export const memberRoutingOnlyState = atom({
    key: 'editing.member.routing',
    default: false,
});

export const editingJobIdState = atom<string | undefined>({
    key: 'editing.job.id',
    default: undefined,
});

export const editingCompanyIdState = atom({
    key: 'editing.company.id',
    default: '',
});

export const editingPlaceIdState = atom<Uid | undefined>({
    key: 'editing.place.id',
    default: undefined,
});

export const editingTeamIdState = atom<number>({
    key: 'editing.team.id',
    default: -1,
});

export const editingZoneIdState = atom<string | undefined>({
    key: 'editing.zone.id',
    default: undefined,
});

export const companyTabFilterState = atom({
    key: 'company.tab.filter',
    default: "" as CompanyTabKeys | "",
});

/** the tab open in the company settings dialog, so its header can offer the film's part for that tab */
export const companyActiveTabState = atom({
    key: 'company.tab.active',
    default: "" as CompanyTabKeys | "",
});

export const modalIsOpenState = selector({
    key: 'app.modal.open',
    get: ({ get }) => {
        const editingMember = get(editingMemberIdState);
        const editingCompany = get(editingCompanyIdState);
        // a trial that ran out shows the plan window to owners and admins instead of company settings
        const accountBlocked = get(accountBlockedState) && !get(trialEndedForAdminState);
        // jobs, places and zones open in their sheets, never in this modal
        return editingMember || editingCompany || accountBlocked
    }
});

export const hiddenModalState = selector({
    key: "app.modal.hidden",
    get: ({ get }) => {
        const drawMode = get(drawModeState);
        const findAssignee = get(findAssigneeForJobState);
        return isLocationDrawMode(drawMode) || findAssignee;
    }
});

export const modalContentState = selector({
    key: 'app.modal.content',
    get: ({ get }) => {
        const editingMember = get(editingMemberIdState);
        const editingCompany = get(editingCompanyIdState);
        const accountBlocked = get(accountBlockedState);

        if (editingCompany || accountBlocked) {
            return 'company';
        }
        if (editingMember) {
            return 'member';
        }
        return '';
    }
});

export const prefAutoZoomState = atomWithPersistence("pref.autozoom", true);

export const addingTeamState = atom({
    key: 'team.editing',
    default: false,
});

export const draggingState = atom({
    key: 'dragging',
    default: false,
});

export const editingJobTypesState = atom({
    key: 'jobTypes.editing',
    default: false,
});

export const importTypeState = atom<ImportType | undefined>({
    key: 'files.import',
    default: undefined,
});

/** A file the job import window reads as soon as it opens, like one dropped onto Dispatch or All jobs. */
export const importFileState = atom<File | undefined>({
    key: 'files.import.file',
    default: undefined,
    dangerouslyAllowMutability: true, // a File is not plain data
});

export const sidebarBrokenState = atom({
    key: "sidebar.broken",
    default: false,
});

export const sidebarCollapsedState = atom({
    key: "sidebar.collapsed",
    default: false,
});

export const sidebarPinnedState = atomWithPersistence("sidebar.pinned", true);

/** The menu shows its labels: the user keeps it open and the space budget has not folded it. */
export const sidebarExpandedState = selector({
    key: "sidebar.expanded",
    get: ({ get }) => get(sidebarPinnedState) && !get(layoutFoldsState).menu,
});

export const sidebarWidthState = selector({
    key: "sidebar.width", get: ({ get }) => {
        const actionIsOpen = get(actionsIsOpenState);
        const broken = get(sidebarBrokenState);
        const collapsed = get(sidebarCollapsedState);
        return !actionIsOpen && !broken ? collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH : 0;
    }
});
export const sidebarOffsetState = selectorFamily<number, boolean>({
    key: "sidebar.offset",
    get: (broken) => ({ get }) => {
        const isGoogle = get(isGoogleProviderState);
        const sidebarWidth = get(sidebarWidthState);
        const sidebarExpanded = get(sidebarExpandedState);
        if (!isGoogle) {
            return (broken || sidebarExpanded) ? 0 : SIDEBAR_COLLAPSED_WIDTH;
        }
        return (broken || sidebarExpanded) ? sidebarWidth : SIDEBAR_COLLAPSED_WIDTH;
    }
});


export const showBeaconState = atom({
    key: "help.beacon.visible",
    default: 0,
});

export const loadedState = selector<boolean>({
    key: "app.loaded", get: ({ get }) => {
        const memberUids = get(memberUidsState);
        return memberUids.length > 0;
    }
});

export const messageApiState = atom<MessageInstance | undefined>({ key: "message.api", default: undefined });

export const notificationApiState = atom<NotificationInstance | undefined>({
    key: "notification.api",
    default: undefined
});

export const routeSimulationRunningForState = atomFamily<boolean, string>({
    key: "app.route.simulation.running",
    default: false,
});

export const snoozeNotificationsState = atomWithPersistence("app.snoozenotifications", false);

export const desktopDownloadDismissedState = atomWithPersistence("app.desktop.download.dismissed", false);

export const ignoreSettingState = atomFamily<boolean, MemberField>({
    key: 'app.setting.ignore',
    default: false,
});

/** the order before a better order was used, kept in the browser so Undo still works after a reload */
export const routingUndoOptimizeState = atomFamily<string[], UidDay>({
    key: 'app.routing.optimize.originalorder',
    default: [],
    effects: ({ uid, day }) => [storageEffectForKey("routing.undo." + uid + "." + day)],
});

/** the order a better order put in place of routingUndoOptimizeState; Undo is right only while the day still has it */
export const routingUndoAfterState = atomFamily<string[], UidDay>({
    key: 'app.routing.optimize.appliedorder',
    default: [],
    effects: ({ uid, day }) => [storageEffectForKey("routing.undo.after." + uid + "." + day)],
});

export type RoutingOptimizedOverride = {
    optimized: boolean;
    ts: Timestamp;
};

export const routingOptimizedOverrideState = atomFamily<RoutingOptimizedOverride | undefined, UidDay>({
    key: "app.routing.optimize.override",
    default: undefined,
});

export const routingRecentlyUnassignedState = atomFamily<string[], UidDay>({
    key: 'app.routing.optimize.unassigned',
    default: [],
});

export const activitiesState = atom({
    key: "app.activities",
    default: [] as Activity[]
});

export const selectedMultiMemberState = atom<Member[]>({
    key: "app.member.selection.multi",
    default: []
});

/**
 * Where a statistics drilldown came from and where it went, so the target page can offer the
 * way back. Both halves are needed: navigation runs through the router and lands a render or
 * two later, so "are we still on the origin?" is not a safe test at the moment the drill fires.
 */
export type DrillReturn = { from: PageType; to: PageType };

export const drillReturnState = atom<DrillReturn | null>({
    key: "app.drill.return",
    default: null,
});

export const hideAllPanelsState = atom<boolean>({
    key: "app.panels.hide",
    default: false
});

export const constraintsState = atomWithPersistence("app.routing.constraints", [
    Constraint.WORKING_HOURS,
    Constraint.TIME_WINDOWS,
    Constraint.SKILLS,
    Constraint.CAPACITY,
    Constraint.EVEN_DISTRIBUTION,
]);

export const blurState = atomWithPersistence("app.blur", !isSafari());

export const demoScheduledState = atomWithPersistence("demo.scheduled", false);

/** Who and where a new job is for, when New job was opened from a member's route. */
export type NewJobContext = {
    uid?: Uid;
    number?: number; // route position for a blank job
    index?: number; // route position for a job made from a template or an earlier job
};

/** Who and where the New job card was opened for; the card itself is open while draw mode has `composer`. */
export const jobComposerContextState = atom<NewJobContext>({
    key: "app.jobComposer.context",
    default: {},
});

/**
 * A new job started from a template or an earlier job. The New job card and the sheet show it filled in, and
 * only Create job makes it, the way a copy is made, so the template's use and the job's origin are kept. Until
 * then nothing exists and no phone hears of it.
 */
export type JobSeed = { kind: "template" | "job"; id: string; job: Job; lastAssigneeUid?: Uid | "" };

export const newJobSeedState = atom<JobSeed | undefined>({
    key: "app.newJob.seed",
    default: undefined,
});

/** The "Start from" step of New job, open while set. */
export const newJobPickerState = atom<NewJobContext | undefined>({
    key: "app.newJobPicker",
    default: undefined,
});

export const selectingTemplateState = atom<{uid: Uid, index: number} | undefined>({
    key: "app.selectingTemplate",
    default: undefined,
});

export const selectingUnassignedState = atom<{uid: Uid, number: number} | undefined>({
    key: "app.selectingUnassigned",
    default: undefined,
});

export const showAssigneeSuggestionsState = atomWithPersistence("app.findassignee.suggestions", true);

export const geocodeCounterState = atomWithPersistence("app.geocode.counter", 0);
export const geocodeTimestampState = atomWithPersistence("app.geocode.timestamp", 0);
